import { Group } from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";

import type { FigureData } from "../data/octagonOnCube";

/**
 * 各頂点に CSS2DObject で「円形 DOM 要素」を配置し、ドットとして描画する。
 * 3D シーンの Points と違い、WebGL の z テストや凸包メッシュの polygonOffset
 * から完全に独立しているため、点が削れたりハネと干渉したりしない。
 *
 * 表示位置は CSS2DRenderer が毎フレーム自動で画面座標に投影してくれる。
 * 可視性 (visible) は main.ts 側で raycaster による正確なオクルージョン判定
 * で per-vertex に制御する。
 */
export class Dots {
  readonly group: Group;
  readonly count: number;
  private readonly elements: HTMLDivElement[];

  constructor(data: FigureData) {
    this.group = new Group();
    this.elements = [];
    this.count = data.vertices.length;
    for (let i = 0; i < data.vertices.length; i++) {
      const [x, y, z] = data.vertices[i];
      const el = document.createElement("div");
      el.className = "vertex-dot";
      const dot = new CSS2DObject(el);
      dot.position.set(x, y, z);
      dot.center.set(0.5, 0.5);
      this.group.add(dot);
      this.elements.push(el);
    }
  }

  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  /** 各ドットの 3D 位置を差し替える (八角形比率の切替用)。 */
  setPositions(vertices: [number, number, number][]): void {
    const children = this.group.children;
    for (let i = 0; i < children.length && i < vertices.length; i++) {
      children[i].position.set(
        vertices[i][0],
        vertices[i][1],
        vertices[i][2],
      );
    }
  }

  setSize(sizePx: number): void {
    for (const el of this.elements) {
      el.style.width = `${sizePx}px`;
      el.style.height = `${sizePx}px`;
    }
  }

  /** 頂点 i のドットだけサイズを変える (heartbeat の per-point パルス用)。 */
  setSizeAt(i: number, sizePx: number): void {
    const el = this.elements[i];
    if (el) {
      el.style.width = `${sizePx}px`;
      el.style.height = `${sizePx}px`;
    }
  }

  setColor(color: string): void {
    for (const el of this.elements) {
      el.style.backgroundColor = color;
    }
  }

  // 24 個の点それぞれに独立した HSL ランダム色を割り当てる。色相全域 / 中明度・
  // 中彩度に絞っているのは、白背景でも暗背景でも沈まず、かつパステルに寄りすぎ
  // ないバランスを取るため。
  setRandomColors(): void {
    for (const el of this.elements) {
      const h = Math.floor(Math.random() * 360);
      const s = 55 + Math.floor(Math.random() * 25); // 55-79
      const l = 45 + Math.floor(Math.random() * 15); // 45-59
      el.style.backgroundColor = `hsl(${h} ${s}% ${l}%)`;
    }
  }

  setOpacity(i: number, value: number): void {
    const el = this.elements[i];
    if (el) el.style.opacity = String(value);
  }
}
